import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { MessageSquareText, Minus, Plus, Sparkles } from 'lucide-react'
import { useCurrentUser } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Quote } from '../api/types'
import { AssistantChat } from './assistant-chat'
import { useAssistantPanel } from './panel-store'

/**
 * The SmartQuote assistant (demo `QuoteAssistant`): one floating panel for the whole dashboard.
 * On a quote it works on that quote; elsewhere it starts new quotes and follows a quote it creates.
 */
export function QuoteAssistant({ quote = null, quoteId }: { quote?: Quote | null; quoteId?: string | null }) {
  const navigate = useNavigate()
  const { open, opened, setOpen } = useAssistantPanel()
  const currentUser = useCurrentUser()
  const [scope, setScope] = useState('global')
  const [activeQuote, setActiveQuote] = useState<Quote | null>(null)
  // The quote this conversation created and navigated to keeps the same conversation.
  const [adopted, setAdopted] = useState<string | null>(null)
  const [seenQuote, setSeenQuote] = useState<Quote | null>(null)
  const restoreButton = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLElement>(null)
  if (quote && quote !== seenQuote) {
    setSeenQuote(quote)
    if (quote.id !== adopted) {
      setAdopted(null)
      setScope(quote.id)
    }
    setActiveQuote(quote)
  }
  useEffect(() => {
    if (open) panel.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus()
  }, [open])
  const minimize = () => {
    setOpen(false)
    requestAnimationFrame(() => restoreButton.current?.focus())
  }
  const current = quoteId ? quote || (activeQuote?.id === quoteId ? activeQuote : null) : activeQuote
  const actorId = currentUser.data?.id
  const onQuoteChange = (next: Quote, navigateToQuote = true) => {
    setAdopted(next.id)
    setActiveQuote(next)
    if (navigateToQuote && next.id !== current?.id) {
      void navigate({ to: '/quotes/$quoteId/workflow/$stage', params: { quoteId: next.id, stage: 'equipment' } })
    }
  }
  return (
    <div className="fixed bottom-4 right-3 z-40 sm:bottom-5 sm:right-5">
      {opened || open ? (
        <section
          aria-label="SmartQuote assistant"
          className={cn(
            open ? 'flex' : 'hidden',
            'h-[min(680px,calc(100dvh-88px))] w-[min(420px,calc(100vw-24px))] flex-col overflow-hidden rounded-[14px] border bg-background shadow-[0_10px_15px_-3px_rgb(0_0_0/0.1),0_4px_6px_-4px_rgb(0_0_0/0.1)]',
          )}
          hidden={!open}
          id="smartquote-assistant"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              minimize()
            }
          }}
          ref={panel}
        >
          <header className="flex items-center gap-2 border-b py-1 pl-4 pr-1">
            <Sparkles aria-hidden="true" className="size-4 text-violet-600" />
            <h2 className="flex-1 text-sm font-semibold">SmartQuote</h2>
            {!quoteId ? (
              <Button
                className="text-xs text-muted-foreground"
                onClick={() => {
                  setAdopted(null)
                  setActiveQuote(null)
                  setScope(`global-${Date.now()}-${Math.random()}`)
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                <Plus />
                New conversation
              </Button>
            ) : null}
            <Button aria-label="Minimize SmartQuote" onClick={minimize} size="icon" title="Minimize (Esc)" type="button" variant="ghost">
              <Minus />
            </Button>
          </header>
          {currentUser.isError ? (
            <p className="p-4 text-sm text-destructive" role="alert">
              Could not load your account.{' '}
              <button className="font-medium underline" onClick={() => void currentUser.refetch()} type="button">
                Retry
              </button>
            </p>
          ) : actorId && (!quoteId || current) ? (
            <AssistantChat
              actorId={actorId}
              key={`${scope}:${actorId}`}
              onQuoteChange={onQuoteChange}
              quote={current}
              scope={scope}
              visible={open}
            />
          ) : (
            <p className="p-4 text-sm text-muted-foreground" role="status">
              Loading...
            </p>
          )}
        </section>
      ) : null}
      {!open ? (
        <Button
          aria-controls="smartquote-assistant"
          aria-expanded={false}
          className="size-11 rounded-full bg-violet-600 text-white shadow-[0_4px_12px_rgb(76_29_149/0.28)] hover:bg-violet-700 sm:w-auto sm:px-4"
          onClick={() => setOpen(true)}
          ref={restoreButton}
          type="button"
        >
          <MessageSquareText />
          <span className="sr-only sm:not-sr-only">{opened ? 'Restore SmartQuote' : 'Ask SmartQuote'}</span>
        </Button>
      ) : null}
    </div>
  )
}
